import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { pubAdminApi } from './api';

const root = ['pub-admin'] as const;
export const pubKeys = {
  dashboard: [...root, 'dashboard'] as QueryKey,
  customers: [...root, 'customers'] as QueryKey,
  buyIns: [...root, 'buy-ins'] as QueryKey,
  prizes: [...root, 'prizes'] as QueryKey,
  bonuses: [...root, 'bonuses'] as QueryKey,
  tournaments: [...root, 'tournaments'] as QueryKey,
  history: [...root, 'history'] as QueryKey,
};

export const usePubDashboard = () => useQuery({ queryKey: pubKeys.dashboard, queryFn: pubAdminApi.dashboard });
export const usePubCustomers = () => useQuery({ queryKey: pubKeys.customers, queryFn: pubAdminApi.customers });
export const usePubBuyIns = () => useQuery({ queryKey: pubKeys.buyIns, queryFn: pubAdminApi.buyIns });
export const usePubPrizes = () => useQuery({ queryKey: pubKeys.prizes, queryFn: pubAdminApi.prizes });
export const usePubBonuses = () => useQuery({ queryKey: pubKeys.bonuses, queryFn: pubAdminApi.bonuses });
export const usePubTournaments = () => useQuery({ queryKey: pubKeys.tournaments, queryFn: pubAdminApi.tournaments });
export const usePubHistory = () => useQuery({ queryKey: pubKeys.history, queryFn: pubAdminApi.history });

// 쓰기 성공 후 영향을 받는 조회를 모두 무효화한다. 거래는 항상 감사 로그(history)와 대시보드 지표에 반영된다.
function useInvalidatingMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  affected: QueryKey[],
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await Promise.all(
        [pubKeys.dashboard, pubKeys.history, ...affected].map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

export const useProcessBuyIn = () =>
  useInvalidatingMutation(pubAdminApi.processBuyIn, [pubKeys.buyIns, pubKeys.customers]);
export const useAwardPrize = () =>
  useInvalidatingMutation(pubAdminApi.awardPrize, [pubKeys.prizes, pubKeys.customers]);
export const useGrantBonus = () =>
  useInvalidatingMutation(pubAdminApi.grantBonus, [pubKeys.bonuses, pubKeys.customers]);
export const useCancelBonus = () =>
  useInvalidatingMutation(pubAdminApi.cancelBonus, [pubKeys.bonuses, pubKeys.customers]);
export const useCreateTournament = () =>
  useInvalidatingMutation(pubAdminApi.createTournament, [pubKeys.tournaments]);
export const useSaveTournamentResults = () =>
  useInvalidatingMutation(pubAdminApi.saveTournamentResults, [pubKeys.tournaments, pubKeys.prizes, pubKeys.customers]);
export const useCompleteTournament = () =>
  useInvalidatingMutation(pubAdminApi.completeTournament, [pubKeys.tournaments]);
